import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  selectSelectedLeague,
  setSelectedLeague,
} from '../redux/slice/selectedLeagueSlice';
import {
  fetchAllLeagues,
  mapLeagueItemToGetAllLeagues,
} from '../views/myLeagues/api/myLeaguesApi';

const LeagueRouteHydrator: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const dispatch = useAppDispatch();
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const alreadyMatches =
    !!selectedLeague && selectedLeague.leagueId === leagueId;
  const dispatchedRef = useRef<string | null>(null);

  const {
    data: leagues,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['myLeagues', 'allLeague'],
    queryFn: fetchAllLeagues,
    enabled: !!leagueId && !alreadyMatches,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!leagueId || alreadyMatches) return;
    if (!leagues) return;
    if (dispatchedRef.current === leagueId) return;
    const match = leagues.find((l) => l.leagueId === leagueId);
    if (match) {
      dispatchedRef.current = leagueId;
      dispatch(setSelectedLeague(mapLeagueItemToGetAllLeagues(match)));
    }
  }, [alreadyMatches, leagueId, leagues, dispatch]);

  if (!leagueId) return <Navigate to="/my-leagues" replace />;
  if (alreadyMatches) return <Outlet />;
  if (isLoading || !leagues) {
    return (
      <div className="myleagues-loading">
        <span>Loading league...</span>
      </div>
    );
  }
  if (isError) return <Navigate to="/my-leagues" replace />;

  const found = leagues.find((l) => l.leagueId === leagueId);
  if (!found) return <Navigate to="/my-leagues" replace />;

  return (
    <div className="myleagues-loading">
      <span>Loading league...</span>
    </div>
  );
};

export default LeagueRouteHydrator;
